import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import WhatsAppButton from "@/components/ui/WhatsAppButton";
import Index from "./pages/Index";
import { initializeAnalytics } from "@/utils/analytics";
import { useDocumentLang } from "@/hooks/useDocumentLang";

// Lazy imports avec fonctions de prefetch exportées
const importSociete = () => import("./pages/Societe");
const importProduits = () => import("./pages/Produits");
const importPartenaires = () => import("./pages/Partenaires");
const importContact = () => import("./pages/Contact");
const importNotFound = () => import("./pages/NotFound");

const Societe = lazy(importSociete);
const Produits = lazy(importProduits);
const Partenaires = lazy(importPartenaires);
const Contact = lazy(importContact);
const NotFound = lazy(importNotFound);

// Fonctions de prefetch exportées pour le Header
export const prefetchPage = (path: string) => {
  switch (path) {
    case '/societe': importSociete(); break;
    case '/produits': importProduits(); break;
    case '/partenaires': importPartenaires(); break;
    case '/contact': importContact(); break;
  }
};

// Composant pour le fallback Suspense traduit
const LoadingFallback = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-pulse text-muted-foreground">{t('common.loading')}</div>
    </div>
  );
};

const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    initializeAnalytics();
  }, []);

  useDocumentLang();

  return (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <div className="min-h-screen bg-background flex flex-col">
            <Header />
            <main className="flex-1">
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/societe" element={<Societe />} />
                  <Route path="/produits" element={<Produits />} />
                  <Route path="/partenaires" element={<Partenaires />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </main>
            <Footer />
            <WhatsAppButton />
          </div>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
  );
};

export default App;
